import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Leaf, Headphones, ShoppingBag, LogOut, ExternalLink, Package, Menu, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { AdminNotificationsProvider, useAdminNotifications } from "../../context/AdminNotificationsContext";

const NAV = [
  { to: "/admin/dashboard",  icon: LayoutDashboard, label: "Dashboard"   },
  { to: "/admin/mushrooms",  icon: Leaf,            label: "Mushrooms"   },
  { to: "/admin/podcasts",   icon: Headphones,      label: "Podcasts"    },
  { to: "/admin/preorders",  icon: ShoppingBag,     label: "Pre-Orders",  pendingKey: "preorders" },
  { to: "/admin/orders",     icon: Package,         label: "Orders",      pendingKey: "orders" },
];

function PendingBadge({ count }) {
  if (!count) return null;
  return (
    <span className="ml-auto flex-shrink-0 min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full text-[10px] font-bold text-white"
      style={{ backgroundColor: '#ef4444' }} data-testid="pending-badge">
      {count > 99 ? "99+" : count}
    </span>
  );
}

function SidebarContent({ admin, onLogout, onClose }) {
  const { pending } = useAdminNotifications();

  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-5 border-b border-[#273A2F] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Leaf className="w-5 h-5 text-[#D97A46]" />
          <div>
            <span className="font-serif font-bold text-[#F2F4F3]">PIIKK</span>
            <span className="text-[10px] text-[#D97A46] ml-1 uppercase tracking-widest">Admin</span>
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} className="p-1.5 rounded-lg text-[#A8BCAE] hover:text-[#F2F4F3] lg:hidden">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>
      <p className="text-xs text-[#A8BCAE] px-5 pt-2 pb-1 truncate">{admin?.email}</p>

      {/* Nav links */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto" data-testid="admin-sidebar">
        {NAV.map(({ to, icon: Icon, label, pendingKey }) => {
          const badgeCount = pendingKey ? pending[pendingKey] : 0;
          return (
            <NavLink
              key={to} to={to}
              onClick={onClose}
              data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${isActive
                  ? "bg-[#D97A46]/15 text-[#D97A46] font-medium"
                  : "text-[#A8BCAE] hover:text-[#F2F4F3] hover:bg-white/5"
                }`
              }
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1">{label}</span>
              <PendingBadge count={badgeCount} />
            </NavLink>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="p-3 border-t border-[#273A2F] space-y-1">
        <a href="/" target="_blank" rel="noreferrer"
          className="flex items-center gap-3 px-3 py-2 rounded-xl text-xs text-[#A8BCAE] hover:text-[#F2F4F3] transition-all">
          <ExternalLink className="w-4 h-4" /> View Store
        </a>
        <button data-testid="admin-logout-btn" onClick={onLogout}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs text-red-400 hover:bg-red-400/10 transition-all">
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </div>
    </div>
  );
}

function AdminLayoutInner() {
  const { logout, admin } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { pending } = useAdminNotifications();
  const totalPending = (pending.orders || 0) + (pending.preorders || 0);

  const handleLogout = () => { logout(); navigate("/admin/login"); };

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: '#0F1612' }}>

      {/* ── Desktop Sidebar ── */}
      <aside className="hidden lg:flex w-60 flex-shrink-0 flex-col border-r border-[#273A2F]" style={{ backgroundColor: '#16201A' }}>
        <SidebarContent admin={admin} onLogout={handleLogout} />
      </aside>

      {/* ── Mobile Sidebar Overlay ── */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 z-40 lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
            <motion.aside
              initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="fixed top-0 left-0 bottom-0 w-64 z-50 flex flex-col border-r border-[#273A2F] lg:hidden"
              style={{ backgroundColor: '#16201A' }}
              data-testid="admin-mobile-sidebar"
            >
              <SidebarContent admin={admin} onLogout={handleLogout} onClose={() => setSidebarOpen(false)} />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* ── Main column ── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

        {/* Mobile top bar */}
        <header className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-[#273A2F] flex-shrink-0" style={{ backgroundColor: '#16201A' }}>
          <button onClick={() => setSidebarOpen(true)}
            className="relative p-2 rounded-xl text-[#A8BCAE] hover:text-[#F2F4F3] hover:bg-white/5 transition-all"
            data-testid="admin-mobile-menu-btn">
            <Menu className="w-5 h-5" />
            {totalPending > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 flex items-center justify-center rounded-full text-[9px] font-bold text-white"
                style={{ backgroundColor: '#ef4444' }} data-testid="mobile-total-pending-badge">
                {totalPending > 9 ? "9+" : totalPending}
              </span>
            )}
          </button>
          <div className="flex items-center gap-1.5">
            <Leaf className="w-4 h-4 text-[#D97A46]" />
            <span className="font-serif font-bold text-[#F2F4F3] text-sm">PIIKK</span>
            <span className="text-[10px] text-[#D97A46] uppercase tracking-widest">Admin</span>
          </div>
          <button onClick={handleLogout} className="p-2 rounded-xl text-red-400 hover:bg-red-400/10 transition-all">
            <LogOut className="w-4 h-4" />
          </button>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  return (
    <AdminNotificationsProvider>
      <AdminLayoutInner />
    </AdminNotificationsProvider>
  );
}
